import React, { memo } from "react";
import Container from "~/components/atoms/Container";
import { PAGE_SECTION_IDS, PAGE_SECTION_SCROLL_CLASS } from "~/lib/constants/pageSectionIds";

const VectorIllustrationHeroSection = memo(({ text, image, logo, description }: { text: string, image: string, logo?: boolean, description?: string }) => {
	// Extract the actual image path from ImageKit URL (handles both direct paths and CloudFront URLs)
	const getImagePath = (imgUrl: string) => {
		if (imgUrl.includes('ik.imagekit.io')) {
			// Match pattern: officehub/tr:.../path or officehub/tr:.../https://...
			const match = imgUrl.match(/officehub\/tr:[^/]+\/(.+)/);
			if (match) {
				return match[1]; // Return everything after the transformation
			}
		}
		// If no match, return the original URL
		return imgUrl.startsWith('http') ? imgUrl : `https://${imgUrl}`;
	};

	const imagePath = getImagePath(image);

	// Generate srcSet matching actual container widths for optimal image sizing
	const generateSrcSet = () => {
		// Match actual container widths more precisely
		const widths = [640, 768, 1024, 1280, 1400]; // Match actual container sizes
		return widths
			.map(w => {
				const h = Math.round(w * (438 / 1400)); // Match aspect ratio to actual rendered size
				// Use progressive quality - lower for smaller, higher for larger
				const quality = w <= 768 ? 70 : w <= 1024 ? 75 : 80;
				return `https://ik.imagekit.io/officehub/tr:w-${w},h-${h},fo-auto,f-avif,q-${quality}/${imagePath} ${w}w`;
			})
			.join(', ');
	};

	// Start with 768px image (typical tablet/desktop container) for faster LCP
	const defaultSrc = `https://ik.imagekit.io/officehub/tr:w-768,h-240,fo-auto,f-avif/${imagePath}`;

	return (
		<section id={PAGE_SECTION_IDS.hero} className={PAGE_SECTION_SCROLL_CLASS}>
		<Container htmlFor="ultra-wide">
			<div className="relative rounded-lg overflow-hidden">
				{/* Background image - Optimized for LCP with smaller initial size */}
				<img
					src={defaultSrc}
					srcSet={generateSrcSet()}
					sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 100vw, (max-width: 1280px) 1280px, 1400px"
					alt="Office Hub team member ready to assist with workspace enquiries in a professional office setting"
					width={1400}
					height={438}
					loading="eager"
					decoding="sync"
					fetchPriority="high"
					className="w-full h-64 md:h-80 lg:h-[500px] object-cover object-top"
					style={{
						contentVisibility: "auto",
						maxWidth: "100%",
						aspectRatio: "1400/438"
					}}
				/>

				{/* Overlay */}
				<div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-center px-4">
					{/* Logo */}
					{logo ? (
						<img
							src="https://ik.imagekit.io/officehub/tr:w-382,h-113,c-maintain_ratio,f-avif/https://d6yg2sphm6jrv.cloudfront.net/img/logo-office-hub-large.png"
							alt="Office Hub Logo"
							width={382}
							height={113}
							loading="eager"
							decoding="async"
							fetchPriority="high"
						/>
					) : null}

					{/* Subheading */}
					<h1 className="mt-2 text-white text-3xl font-medium">
						{text}
					</h1>
					{description && (
						<p className="mt-2 text-white text-md">
							{description}
						</p>
					)}
				</div>
			</div>
		</Container>
		</section>
	);
});

VectorIllustrationHeroSection.displayName = "VectorIllustrationHeroSection";

export default VectorIllustrationHeroSection; 